import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPublicPageContents, type PageContent } from '@/services/contentService';
import { systemContentConfig } from '@/config/systemContent.config';

interface SystemContentContextType {
    content: Record<string, string>; // key -> localized title
    isLoading: boolean;
    refresh: () => Promise<void>;
}

const SystemContentContext = createContext<SystemContentContextType | undefined>(undefined);

// Cache for content (5 minute TTL)
let contentCache: PageContent[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function SystemContentProvider({ children }: { children: ReactNode }) {
    const { i18n } = useTranslation();
    const currentLang = i18n.language as string;

    const [contentMap, setContentMap] = useState<Record<string, string>>(() => {
        const initialMap: Record<string, string> = {};
        systemContentConfig.forEach(section => {
            section.items.forEach(item => {
                initialMap[item.key] = item.defaultValue || '';
            });
        });
        return initialMap;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [rawContent, setRawContent] = useState<PageContent[]>([]);

    // 1. Fetch all content once
    useEffect(() => {
        fetchContent();
    }, []);

    // 2. Update map when language changes or raw content updates
    useEffect(() => {
        // if (rawContent.length === 0) return; // Allow updates even if empty to ensure defaults are there? Actually defaults are in init state now.
        // But we want to re-run this if language changes to merge defaults + rawContent (if any)

        const newMap: Record<string, string> = {};

        // Fill with defaults from config first (fallback)
        systemContentConfig.forEach(section => {
            section.items.forEach(item => {
                newMap[item.key] = item.defaultValue || '';
            });
        });

        // Override with DB content (only show translated content, no fallback)
        rawContent.forEach(item => {
            let title = item.title?.[currentLang];

            // Fallback if missing in current lang
            if (!title && item.title) {
                title = item.title['en'] || Object.values(item.title)[0];
            }

            // Ensure it's a string
            if (title && typeof title === 'object') {
                // If double wrapped or something, try to get string
                title = (title as any)[currentLang] || (title as any)['en'] || JSON.stringify(title);
            }

            if (title && typeof title === 'string') {
                newMap[item.key] = title;
            }
        });

        setContentMap(newMap);
    }, [currentLang, rawContent]);

    const fetchContent = async () => {
        // Check if cache is still valid
        if (contentCache && cacheTimestamp) {
            const now = Date.now();
            if (now - cacheTimestamp < CACHE_DURATION) {
                setRawContent(contentCache);
                setIsLoading(false);
                return;
            }
        }

        setIsLoading(true);
        try {
            const data = await fetchPublicPageContents();

            // Update cache
            contentCache = data;
            cacheTimestamp = Date.now();

            setRawContent(data);
        } catch (error) {
            console.error("Failed to load system content", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <SystemContentContext.Provider value={{ content: contentMap, isLoading, refresh: fetchContent }}>
            {children}
        </SystemContentContext.Provider>
    );
}

export function useSystemContent() {
    const context = useContext(SystemContentContext);
    if (context === undefined) {
        throw new Error('useSystemContent must be used within a SystemContentProvider');
    }
    return context;
}

/**
 * Clear the system content cache
 */
export const clearSystemContentCache = () => {
    contentCache = null;
    cacheTimestamp = null;
    console.log('[System Content] Cache cleared');
};
